import React, { useEffect, useMemo, useState } from 'react';
import { InlineLoading, InlineNotification } from '@carbon/react';
import moment, { Moment } from 'moment';
import './CompliancePlayer.scss';
import CalendarPanel from './CalendarPanel';
import RecordedList from './RecordedList';
import PlaybackPanel from './PlaybackPanel';
import ChannelList from './ChannelList';
import { RecordedItem } from './CompliancePlayer.types';
import { complianceApi, ComplianceChoice, ComplianceRecording } from '../../../data/complianceApi';

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 1) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const unit = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** unit).toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
};

const toRecordedItem = (item: ComplianceRecording): RecordedItem => ({
  id: item.id,
  title: item.fileName.replace(/\.[^.]+$/, ''),
  streamUrl: item.streamUrl,
  channel: item.channel,
  size: formatBytes(item.sizeBytes),
  date: moment(item.createdAt).format('YYYY-MM-DD HH:mm:ss'),
  createdAt: item.createdAt,
  jobId: item.jobId,
  modifiedAt: item.modifiedAt,
});

const CompliancePlayer: React.FC = () => {
  const [recordedItems, setRecordedItems] = useState<RecordedItem[]>([]);
  const [configuredSources, setConfiguredSources] = useState<ComplianceChoice[]>([]);
  const [hlsPlaylistWindowMs, setHlsPlaylistWindowMs] = useState(15 * 60 * 1000);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [selectedDate, setSelectedDate] = useState<Moment>(moment());
  const [timeRange, setTimeRange] = useState({ start: '', end: '' });
  const [selectedRecordedId, setSelectedRecordedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [readyHlsUrl, setReadyHlsUrl] = useState('');

  useEffect(() => {
    let active = true;
    complianceApi.getSettings()
      .then((settings) => {
        if (!active) return;
        setConfiguredSources(settings.sources);
        setHlsPlaylistWindowMs(settings.hlsPlaylistWindowMs || 15 * 60 * 1000);
      })
      .catch((reason) => active && setError(reason instanceof Error ? reason.message : String(reason)));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const load = async (showLoading: boolean) => {
      if (showLoading) setLoading(true);
      const from = selectedDate.clone().startOf('day');
      const to = selectedDate.clone().endOf('day');
      if (timeRange.start) {
        const [hour, minute] = timeRange.start.split(':').map(Number);
        from.set({ hour, minute, second: 0, millisecond: 0 });
      }
      if (timeRange.end) {
        const [hour, minute] = timeRange.end.split(':').map(Number);
        to.set({ hour, minute, second: 59, millisecond: 999 });
      }
      try {
        const result = await complianceApi.getRecordings(from.toISOString(), to.toISOString());
        if (!active) return;
        setRecordedItems(result.items.filter((item) => item.sizeBytes > 0).map(toRecordedItem));
        setError('');
      } catch (reason) {
        if (active) setError(reason instanceof Error ? reason.message : String(reason));
      } finally {
        if (active) setLoading(false);
      }
    };
    void load(true);
    const timer = window.setInterval(() => void load(false), 10000);
    return () => { active = false; window.clearInterval(timer); };
  }, [selectedDate, timeRange.start, timeRange.end]);

  const availableChannels = useMemo(
    () => Array.from(new Set([...configuredSources.map((source) => source.name), ...recordedItems.map((item) => item.channel)])),
    [configuredSources, recordedItems],
  );

  useEffect(() => {
    if (!selectedChannel || !availableChannels.includes(selectedChannel)) setSelectedChannel(availableChannels[0] ?? '');
  }, [availableChannels, selectedChannel]);

  const channelCounts = useMemo(() => {
    const counts: Record<string, number> = Object.fromEntries(availableChannels.map((channel) => [channel, 0]));
    recordedItems.forEach((item) => { counts[item.channel] = (counts[item.channel] ?? 0) + 1; });
    return counts;
  }, [availableChannels, recordedItems]);

  const filteredItems = useMemo(
    () => recordedItems.filter((item) => !selectedChannel || item.channel === selectedChannel),
    [recordedItems, selectedChannel],
  );

  useEffect(() => {
    if (!filteredItems.length) setSelectedRecordedId(null);
    else if (!selectedRecordedId || !filteredItems.some((item) => item.id === selectedRecordedId)) {
      setSelectedRecordedId(filteredItems[0].id);
    }
  }, [filteredItems, selectedRecordedId]);

  const selectedRecorded = filteredItems.find((item) => item.id === selectedRecordedId) ?? filteredItems[0];
  const playbackRange = useMemo(() => {
    const from = selectedDate.clone().startOf('day');
    const to = selectedDate.clone().endOf('day');
    if (timeRange.start) {
      const [hour, minute] = timeRange.start.split(':').map(Number);
      from.set({ hour, minute, second: 0, millisecond: 0 });
    }
    if (timeRange.end) {
      const [hour, minute] = timeRange.end.split(':').map(Number);
      to.set({ hour, minute, second: 59, millisecond: 999 });
    }
    return { from, to };
  }, [selectedDate, timeRange.start, timeRange.end]);
  const selectedSourceId = configuredSources.find((source) => source.name === selectedChannel)?.id;
  const selectedStart = selectedRecorded ? moment(selectedRecorded.createdAt) : playbackRange.from;
  const continuousFrom = selectedStart.isAfter(playbackRange.from) ? selectedStart : playbackRange.from;
  const windowEnd = continuousFrom.clone().add(hlsPlaylistWindowMs, 'milliseconds');
  const continuousTo = windowEnd.isBefore(playbackRange.to) ? windowEnd : playbackRange.to;
  const hlsUrl = selectedSourceId
    ? complianceApi.getHlsPlaylistUrl(selectedSourceId, continuousFrom.toISOString(), continuousTo.toISOString())
    : undefined;
  const rangeLabel = `${continuousFrom.format('YYYY-MM-DD HH:mm:ss')} – ${continuousTo.format('YYYY-MM-DD HH:mm:ss')} (15 min)`;

  useEffect(() => {
    let active = true;
    if (!hlsUrl) {
      setReadyHlsUrl('');
      return () => { active = false; };
    }
    setReadyHlsUrl('');
    const check = async () => {
      try {
        const ready = await complianceApi.isHlsPlaylistReady(hlsUrl);
        if (active) setReadyHlsUrl(ready ? hlsUrl : '');
      } catch {
        if (active) setReadyHlsUrl('');
      }
    };
    void check();
    const timer = window.setInterval(() => void check(), 5000);
    return () => { active = false; window.clearInterval(timer); };
  }, [hlsUrl]);

  return (
    <div className="compliance-player">
      {error && <InlineNotification kind="error" title="Compliance service error" subtitle={error} lowContrast hideCloseButton />}
      {loading && <InlineLoading description="Loading recordings…" />}
      <div className="compliance-player__columns">
        <div className="compliance-player__left">
          <ChannelList channels={availableChannels} selectedChannel={selectedChannel} onSelectChannel={setSelectedChannel} channelCounts={channelCounts} />
        </div>
        <div className="compliance-player__right">
          <div className="compliance-player__right-columns">
            <div className="compliance-player__right-left">
              <CalendarPanel
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                startTime={timeRange.start}
                endTime={timeRange.end}
                onTimeChange={(key, value) => setTimeRange((previous) => ({ ...previous, [key]: value }))}
              />
              <RecordedList items={filteredItems} selectedId={selectedRecorded?.id} onSelect={setSelectedRecordedId} />
            </div>
            <PlaybackPanel item={selectedRecorded} hlsUrl={readyHlsUrl || undefined} hlsPending={Boolean(hlsUrl && !readyHlsUrl)} rangeLabel={rangeLabel} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompliancePlayer;
